import React, { useState, useEffect, useCallback } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Grid,
  Chip,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
  TextField,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import StarIcon from "@mui/icons-material/Star";
import PersonOffOutlinedIcon from "@mui/icons-material/PersonOffOutlined";
import PersonOutlinedIcon from "@mui/icons-material/PersonOutlined";
import LockOpenOutlinedIcon from "@mui/icons-material/LockOpenOutlined";
import LockResetOutlinedIcon from "@mui/icons-material/LockResetOutlined";
import LogoutOutlinedIcon from "@mui/icons-material/LogoutOutlined";
import {
  getEmployeeInfo,
  getEmployeeLocations,
  assignEmployeeLocation,
  removeEmployeeLocation,
} from "../../../../utils/services/control/masterDataService";
import {
  deactivateUser,
  reactivateUser,
  unlockAccount,
  adminResetPassword,
  adminForceLogout,
} from "../../../../utils/services/control/userManagementService";
import PcpConfigPanel from "./PcpConfigPanel";
import ConfirmDialog from "../common/ConfirmDialog";

const InfoRow = ({ label, value }) => (
  <Grid size={{ xs: 12, sm: 6 }}>
    <Typography variant="caption" color="text.secondary">
      {label}
    </Typography>
    <Typography variant="body2">{value || "—"}</Typography>
  </Grid>
);

const EmployeeDetailDialog = ({ open, onClose, employee, allLocations = [], onUserChanged }) => {
  const [info, setInfo] = useState(null);
  const [empLocations, setEmpLocations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [addLocationId, setAddLocationId] = useState("");
  const [saving, setSaving] = useState(false);
  const [removeConfirm, setRemoveConfirm] = useState(null);
  const [userAction, setUserAction] = useState(null);
  const [resetPwdOpen, setResetPwdOpen] = useState(false);
  const [resetPwdValue, setResetPwdValue] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const loadData = useCallback(async () => {
    if (!employee) return;
    setLoading(true);
    try {
      const [infoData, locsData] = await Promise.all([
        getEmployeeInfo(employee.employeeId),
        getEmployeeLocations(employee.employeeId),
      ]);
      setInfo(Array.isArray(infoData) ? infoData[0] : infoData);
      setEmpLocations(Array.isArray(locsData) ? locsData : []);
    } catch (err) {
      console.error("Error loading employee detail:", err);
    } finally {
      setLoading(false);
    }
  }, [employee]);

  useEffect(() => {
    if (open && employee) {
      loadData();
      setError("");
      setSuccess("");
      setAddLocationId("");
    }
  }, [open, employee, loadData]);

  const handleAssignLocation = async () => {
    if (!addLocationId) return;
    setSaving(true);
    setError("");
    try {
      const res = await assignEmployeeLocation({
        employeeId: employee.employeeId,
        locationId: Number(addLocationId),
        primary: false,
      });
      if (res.result !== 0) {
        setError(res.message);
      } else {
        setAddLocationId("");
        loadData();
      }
    } catch (err) {
      setError(err.response?.data?.message || "Error assigning location");
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveLocation = async () => {
    setSaving(true);
    setError("");
    try {
      const res = await removeEmployeeLocation({
        employeeId: employee.employeeId,
        locationId: removeConfirm.locationId,
      });
      if (res.result !== 0) {
        setError(res.message);
      } else {
        loadData();
      }
    } catch (err) {
      setError(err.response?.data?.message || "Error removing location");
    } finally {
      setSaving(false);
      setRemoveConfirm(null);
    }
  };

  // User account actions
  const handleUserAction = async () => {
    setSaving(true);
    setError("");
    setSuccess("");
    try {
      let res;
      switch (userAction) {
        case "deactivate":
          res = await deactivateUser(employee.employeeId);
          break;
        case "reactivate":
          res = await reactivateUser(employee.employeeId);
          break;
        case "unlock":
          res = await unlockAccount(employee.employeeId);
          break;
        case "forceLogout":
          res = await adminForceLogout({
            targetEmployeeId: employee.employeeId,
            reason: "Admin forced logout from employee detail",
          });
          break;
        default:
          return;
      }
      if (res.result !== 0) {
        setError(res.message);
      } else {
        setSuccess(res.message || "Action completed successfully");
        onUserChanged?.();
      }
    } catch (err) {
      setError(err.response?.data?.message || "Error performing action");
    } finally {
      setSaving(false);
      setUserAction(null);
    }
  };

  const handleResetPassword = async () => {
    if (!resetPwdValue.trim()) return;
    setSaving(true);
    setError("");
    setSuccess("");
    try {
      const res = await adminResetPassword({
        employeeId: employee.employeeId,
        tempPassword: resetPwdValue,
      });
      if (res.result !== 0) {
        setError(res.message);
      } else {
        setSuccess("Password reset successfully");
        setResetPwdOpen(false);
        setResetPwdValue("");
      }
    } catch (err) {
      setError(err.response?.data?.message || "Error resetting password");
    } finally {
      setSaving(false);
    }
  };

  const availableLocations = allLocations.filter(
    (l) =>
      l.status === 1 &&
      !empLocations.some((el) => el.locationId === l.locationId)
  );

  const userActionMessages = {
    deactivate: `Deactivate user account for ${employee?.firstName} ${employee?.lastName}? Their active session will be closed.`,
    reactivate: `Reactivate user account for ${employee?.firstName} ${employee?.lastName}? They will need to change their password.`,
    unlock: `Unlock account for ${employee?.firstName} ${employee?.lastName}?`,
    forceLogout: `Force logout for ${employee?.firstName} ${employee?.lastName}? Their current session will be terminated.`,
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        Employee Detail — {employee?.firstName} {employee?.lastName}
      </DialogTitle>
      <DialogContent dividers>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {error && (
              <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
                {error}
              </Alert>
            )}
            {success && (
              <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess("")}>
                {success}
              </Alert>
            )}

            {/* General Information */}
            <Typography variant="subtitle2" fontWeight={600} gutterBottom>
              General Information
            </Typography>
            <Grid container spacing={1.5} sx={{ mb: 2 }}>
              <InfoRow label="Employee ID" value={employee?.employeeId} />
              <InfoRow
                label="Full Name"
                value={`${info?.firstName || employee?.firstName} ${info?.lastName || employee?.lastName}`}
              />
              <InfoRow label="Personal Email" value={info?.mail || employee?.mail} />
              <InfoRow
                label="Business Email"
                value={info?.businessEmail || employee?.businessEmail}
              />
              <InfoRow
                label="Job Position"
                value={info?.jobPositionName || employee?.jobPositionName}
              />
              <InfoRow
                label="Role"
                value={info?.roleName || "No role assigned"}
              />
            </Grid>

            <Divider sx={{ my: 2 }} />

            {/* User Account Section */}
            <Typography variant="subtitle2" fontWeight={600} gutterBottom>
              User Account
            </Typography>
            {employee?.hasUser ? (
              <Box
                sx={{
                  display: "flex",
                  gap: 1,
                  alignItems: "center",
                  flexWrap: "wrap",
                  mb: 2,
                }}
              >
                <Chip label="Has account" size="small" color="success" />
                <Tooltip title="Deactivate User">
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => setUserAction("deactivate")}
                  >
                    <PersonOffOutlinedIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Unlock Account">
                  <IconButton
                    size="small"
                    color="warning"
                    onClick={() => setUserAction("unlock")}
                  >
                    <LockOpenOutlinedIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Reset Password">
                  <IconButton
                    size="small"
                    onClick={() => {
                      setResetPwdOpen(true);
                      setResetPwdValue("");
                    }}
                  >
                    <LockResetOutlinedIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Force Logout">
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => setUserAction("forceLogout")}
                  >
                    <LogoutOutlinedIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                No user account. Use the "Create User" button in the employees table.
              </Typography>
            )}

            {/* Reset Password inline dialog */}
            {resetPwdOpen && (
              <Box
                sx={{
                  display: "flex",
                  gap: 1,
                  alignItems: "flex-start",
                  mb: 2,
                  p: 1.5,
                  border: 1,
                  borderColor: "divider",
                  borderRadius: 1,
                }}
              >
                <TextField
                  label="New Temporary Password"
                  value={resetPwdValue}
                  onChange={(e) => setResetPwdValue(e.target.value)}
                  size="small"
                  type="password"
                  sx={{ flex: 1 }}
                  helperText="User will be required to change this on next login."
                />
                <Button
                  variant="contained"
                  size="small"
                  onClick={handleResetPassword}
                  disabled={saving || !resetPwdValue.trim()}
                  sx={{ mt: 0.5 }}
                >
                  Reset
                </Button>
                <Button
                  size="small"
                  onClick={() => setResetPwdOpen(false)}
                  disabled={saving}
                  sx={{ mt: 0.5 }}
                >
                  Cancel
                </Button>
              </Box>
            )}

            <Divider sx={{ my: 2 }} />

            {/* Assigned Locations */}
            <Typography variant="subtitle2" fontWeight={600} gutterBottom>
              Assigned Locations ({empLocations.length})
            </Typography>

            <TableContainer sx={{ mb: 1 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Location</TableCell>
                    <TableCell>Address</TableCell>
                    <TableCell align="center">Primary</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {empLocations.map((loc) => (
                    <TableRow key={loc.locationId}>
                      <TableCell>{loc.medicalCenter}</TableCell>
                      <TableCell>
                        <Typography variant="caption">
                          {loc.addressOffice || "—"}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        {loc.isPrimary && (
                          <StarIcon fontSize="small" color="warning" />
                        )}
                      </TableCell>
                      <TableCell>
                        <Tooltip title="Remove Location">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => setRemoveConfirm(loc)}
                          >
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                  {empLocations.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={4} align="center">
                        <Typography variant="body2" color="text.secondary">
                          No locations assigned
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            <Box sx={{ display: "flex", gap: 1, alignItems: "center", mb: 2 }}>
              <FormControl size="small" sx={{ minWidth: 220 }}>
                <InputLabel>Add Location</InputLabel>
                <Select
                  value={addLocationId}
                  label="Add Location"
                  onChange={(e) => setAddLocationId(e.target.value)}
                >
                  {availableLocations.map((l) => (
                    <MenuItem key={l.locationId} value={l.locationId}>
                      {l.medicalCenter}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Button
                size="small"
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={handleAssignLocation}
                disabled={!addLocationId || saving}
              >
                Assign
              </Button>
            </Box>

            {/* PCP Config */}
            {employee?.isPcp && (
              <>
                <Divider sx={{ my: 2 }} />
                <PcpConfigPanel
                  employee={employee}
                  locations={allLocations}
                />
              </>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>

      {/* Remove Location Confirm */}
      <ConfirmDialog
        open={Boolean(removeConfirm)}
        title="Remove Location"
        message={`Remove ${removeConfirm?.medicalCenter} from this employee?`}
        confirmLabel="Remove"
        onConfirm={handleRemoveLocation}
        onCancel={() => setRemoveConfirm(null)}
        loading={saving}
      />

      {/* User Action Confirm */}
      <ConfirmDialog
        open={Boolean(userAction)}
        title={
          userAction === "deactivate"
            ? "Deactivate User"
            : userAction === "reactivate"
            ? "Reactivate User"
            : userAction === "unlock"
            ? "Unlock Account"
            : "Force Logout"
        }
        message={userAction ? userActionMessages[userAction] : ""}
        confirmLabel={
          userAction === "deactivate"
            ? "Deactivate"
            : userAction === "reactivate"
            ? "Reactivate"
            : userAction === "unlock"
            ? "Unlock"
            : "Force Logout"
        }
        confirmColor={
          userAction === "reactivate" || userAction === "unlock"
            ? "success"
            : "error"
        }
        onConfirm={handleUserAction}
        onCancel={() => setUserAction(null)}
        loading={saving}
      />
    </Dialog>
  );
};

export default EmployeeDetailDialog;
